/*
 * Link bridge between the standalone Assistant page (/view/assistant, its own browser tab) and the
 * main Kawaconn app. Links clicked in the standalone page (case chips, "Open in list view",
 * briefing items, booking hand-offs) must navigate the MAIN app, so the page sends them:
 *
 *  1. to its linked app tab — the tab that opened it (window.opener), or the tab it opened
 *     itself after the original one was closed — via postMessage;
 *  2. else to the Kawaconn tab used most recently (found over a BroadcastChannel);
 *  3. else nothing is open: the page asks the user, and on "yes" opens the link in a new tab
 *     and links itself to that tab (openInNewAppTab).
 *
 * The main app listens with startAssistantBridge (mounted by the Assistant widget).
 */

export const ASSISTANT_PAGE_PATH = '/view/assistant'

const NAVIGATE = 'kawaconn-assistant:navigate'
const PING = 'kawaconn-assistant:ping'
const PONG = 'kawaconn-assistant:pong'
const CHANNEL = 'kawaconn-assistant'
const PING_TIMEOUT_MS = 250

/** Where to go in the main app. */
export type AssistantNavigationTarget =
  | {
      kind: 'route'
      route: string
      query?: Record<string, string> | null
      title?: string
    }
  | {
      /** A dashboard route config (e.g. ROUTE_PATH.assessment.detail(...)) or a path. */
      kind: 'record'
      target: any
      options?: any
    }

/** How the link was delivered; 'none' = no Kawaconn tab is open (ask the user). */
export type AssistantDelivery = 'linked-tab' | 'app-tab' | 'none'

let standalone = false

/** The app tab this page opened itself, once the original tab was closed. */
let linkedAppWindow: Window | null = null

/** True inside the standalone Assistant page (set by that page). */
export const isStandaloneAssistant = () => standalone

export const setStandaloneAssistant = (value: boolean) => {
  standalone = value
}

const channel = (): BroadcastChannel | null =>
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL) : null

/** Plain URL of a target (for the new-tab fallback). */
export const targetPath = (target: AssistantNavigationTarget): string => {
  if (target.kind === 'route') {
    const qs = new URLSearchParams(target.query ?? {}).toString()
    return qs ? `${target.route}?${qs}` : target.route
  }
  return typeof target.target === 'string'
    ? target.target
    : target.target?.path ?? '/'
}

/** The Kawaconn tab used most recently, or null when none answers in time. */
const findAppTab = (): Promise<string | null> => {
  const bc = channel()
  if (!bc) {
    return Promise.resolve(null)
  }
  const requestId = Math.random().toString(36).slice(2)
  const answers: { tabId: string; lastFocus: number }[] = []
  return new Promise(resolve => {
    bc.onmessage = e => {
      if (e.data?.type === PONG && e.data.requestId === requestId) {
        answers.push(e.data)
      }
    }
    bc.postMessage({ type: PING, requestId })
    setTimeout(() => {
      bc.close()
      answers.sort((a, b) => b.lastFocus - a.lastFocus)
      resolve(answers[0]?.tabId ?? null)
    }, PING_TIMEOUT_MS)
  })
}

/** Standalone page → main app. Never opens a tab by itself (see openInNewAppTab). */
export const sendToApp = async (
  navigation: AssistantNavigationTarget
): Promise<AssistantDelivery> => {
  // Links often carry MobX observables (e.g. a briefing item's query), which postMessage cannot
  // clone — send plain data.
  const target = JSON.parse(
    JSON.stringify(navigation)
  ) as AssistantNavigationTarget

  for (const linked of [linkedAppWindow, window.opener as Window | null]) {
    if (linked && !linked.closed) {
      try {
        linked.postMessage({ type: NAVIGATE, target }, window.location.origin)
        linked.focus()
        return 'linked-tab'
      } catch {
        // gone or not ours — try the next one
      }
    }
  }

  const tabId = await findAppTab()
  if (tabId) {
    const bc = channel()
    bc?.postMessage({ type: NAVIGATE, tabId, target })
    bc?.close()
    return 'app-tab'
  }

  return 'none'
}

/**
 * No Kawaconn tab was open and the user agreed: open the link in a new tab and send the next
 * links there. Call it from the user's click (browsers block pop-ups otherwise).
 */
export const openInNewAppTab = (target: AssistantNavigationTarget) => {
  linkedAppWindow = window.open(targetPath(target), '_blank')
  return linkedAppWindow != null
}

/**
 * Main app: navigate when the standalone page asks. Returns the cleanup. Only messages from this
 * origin with the bridge's type are handled.
 */
export const startAssistantBridge = (
  navigate: (_target: AssistantNavigationTarget) => void
) => {
  const tabId = Math.random().toString(36).slice(2)
  let lastFocus = document.hasFocus() ? Date.now() : 0
  const onFocus = () => {
    lastFocus = Date.now()
  }

  const onMessage = (e: MessageEvent) => {
    if (e.origin === window.location.origin && e.data?.type === NAVIGATE) {
      navigate(e.data.target)
    }
  }

  const bc = channel()
  if (bc) {
    bc.onmessage = e => {
      if (e.data?.type === PING) {
        bc.postMessage({
          type: PONG,
          requestId: e.data.requestId,
          tabId,
          lastFocus,
        })
      } else if (e.data?.type === NAVIGATE && e.data.tabId === tabId) {
        navigate(e.data.target)
      }
    }
  }

  window.addEventListener('focus', onFocus)
  window.addEventListener('message', onMessage)
  return () => {
    window.removeEventListener('focus', onFocus)
    window.removeEventListener('message', onMessage)
    bc?.close()
  }
}
